import { COLOR_OPTIONS_BY_NAME, getColorOption } from "./colorPalette"
import { FONT_OPTIONS_BY_NAME, getFontOption } from "./fontPalette"

export type ThemeMode = "light" | "dark" | "system"

export interface AppearanceSettings {
  theme_mode: ThemeMode
  primary_color: string
  secondary_color: string
  heading_font: string
  body_font: string
}

export const DEFAULT_APPEARANCE: AppearanceSettings = {
  theme_mode: "system",
  primary_color: "indigo",
  secondary_color: "amber",
  heading_font: "inter",
  body_font: "inter",
}

export function isValidThemeMode(value: unknown): value is ThemeMode {
  return value === "light" || value === "dark" || value === "system"
}

export function resolveDarkMode(mode: ThemeMode): boolean {
  if (mode === "dark") return true
  if (mode === "light") return false
  if (typeof window === "undefined") return false
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

/**
 * Apply the given appearance settings to the document. Toggles the `.dark`
 * class on <html> and sets CSS variables for primary/secondary colors so the
 * rest of the UI (which reads --primary, --secondary, etc.) picks them up.
 */
export function applyAppearance(appearance: AppearanceSettings): void {
  if (typeof document === "undefined") return

  const root = document.documentElement
  const isDark = resolveDarkMode(appearance.theme_mode)

  root.classList.toggle("dark", isDark)

  const primary = getColorOption(appearance.primary_color)
  const secondary = getColorOption(appearance.secondary_color)

  const primaryShade = isDark ? primary.dark : primary.light
  const secondaryShade = isDark ? secondary.dark : secondary.light

  root.style.setProperty("--primary", primaryShade.bg)
  root.style.setProperty("--primary-foreground", primaryShade.fg)
  root.style.setProperty("--ring", primaryShade.bg)
  root.style.setProperty("--sidebar-primary", primaryShade.bg)
  root.style.setProperty("--sidebar-primary-foreground", primaryShade.fg)
  root.style.setProperty("--sidebar-ring", primaryShade.bg)

  root.style.setProperty("--secondary", secondaryShade.bg)
  root.style.setProperty("--secondary-foreground", secondaryShade.fg)

  const headingFont = getFontOption(appearance.heading_font)
  const bodyFont = getFontOption(appearance.body_font)
  root.style.setProperty("--font-heading", headingFont.family)
  root.style.setProperty("--font-body", bodyFont.family)
}

/**
 * Read the appearance settings that were serialized into the initial page
 * from Inertia shared props. Used for applying the theme before React mounts
 * to avoid a flash of unstyled content.
 */
export function readInitialAppearance(): AppearanceSettings {
  if (typeof document === "undefined") return DEFAULT_APPEARANCE
  const dataEl = document.querySelector("[data-page]")
  if (!dataEl) return DEFAULT_APPEARANCE
  const pageJson = dataEl.getAttribute("data-page")
  if (!pageJson) return DEFAULT_APPEARANCE
  try {
    const page = JSON.parse(pageJson) as {
      props?: { appearance?: Partial<AppearanceSettings> }
    }
    const shared = page.props?.appearance
    if (!shared) return DEFAULT_APPEARANCE
    return {
      theme_mode: isValidThemeMode(shared.theme_mode) ? shared.theme_mode : "system",
      primary_color:
        typeof shared.primary_color === "string" && COLOR_OPTIONS_BY_NAME[shared.primary_color]
          ? shared.primary_color
          : DEFAULT_APPEARANCE.primary_color,
      secondary_color:
        typeof shared.secondary_color === "string" && COLOR_OPTIONS_BY_NAME[shared.secondary_color]
          ? shared.secondary_color
          : DEFAULT_APPEARANCE.secondary_color,
      heading_font:
        typeof shared.heading_font === "string" && FONT_OPTIONS_BY_NAME[shared.heading_font]
          ? shared.heading_font
          : DEFAULT_APPEARANCE.heading_font,
      body_font:
        typeof shared.body_font === "string" && FONT_OPTIONS_BY_NAME[shared.body_font]
          ? shared.body_font
          : DEFAULT_APPEARANCE.body_font,
    }
  } catch {
    return DEFAULT_APPEARANCE
  }
}
